import { jsx, jsxs } from "react/jsx-runtime";
import React__default from "react";
import { cn } from "../../lib/utils.js";
import { transformCompleteConfig } from "../../utils/config-transformer.js";
import { renderBlock } from "./block-registry.js";
const getGridSpanClass = (span) => {
  const gridClasses = {
    1: "col-span-1",
    2: "col-span-2",
    3: "col-span-3",
    4: "col-span-4",
    5: "col-span-5",
    6: "col-span-6",
    7: "col-span-7",
    8: "col-span-8",
    9: "col-span-9",
    10: "col-span-10",
    11: "col-span-11",
    12: "col-span-12"
  };
  return gridClasses[Math.min(span, 12)] || "col-span-12";
};
const DynamicLayoutManager = ({
  areas,
  contextData = {},
  components,
  componentProps,
  isLoading = false,
  loadingComponent: LoadingComponent,
  className,
  fullWidth = false
}) => {
  if (isLoading && LoadingComponent) {
    return /* @__PURE__ */ jsx(LoadingComponent, {});
  }
  if (!areas || areas.length === 0) {
    return /* @__PURE__ */ jsx("div", { className: cn("min-h-screen bg-background", className), children: /* @__PURE__ */ jsx("div", { className: cn(
      "mx-auto py-4",
      !fullWidth && "max-w-7xl"
    ), children: /* @__PURE__ */ jsx("div", { className: "text-center text-muted-foreground", children: /* @__PURE__ */ jsx("p", { children: "No layout configuration found. Please configure your page layout." }) }) }) });
  }
  const areasByRow = areas.reduce((acc, area) => {
    const row = area.grid_row;
    if (!acc[row]) {
      acc[row] = [];
    }
    acc[row].push(area);
    return acc;
  }, {});
  const stickyBlocks = [];
  const regularRows = {};
  Object.keys(areasByRow).forEach((rowKey) => {
    const rowAreas = areasByRow[Number(rowKey)];
    if (!rowAreas || !Array.isArray(rowAreas)) {
      return;
    }
    const hasFullWidthOrSticky = rowAreas.some(
      (area) => area.blocks?.some((block) => {
        const config = block.block_config || {};
        return config.sticky_header === "true" || config.full_width === "true";
      })
    );
    if (hasFullWidthOrSticky && rowAreas.length === 1 && rowAreas[0].blocks?.length === 1) {
      stickyBlocks.push(rowAreas[0].blocks[0]);
    } else {
      regularRows[Number(rowKey)] = rowAreas;
    }
  });
  return /* @__PURE__ */ jsxs("div", { className: cn("min-h-screen bg-background", className), children: [
    stickyBlocks.map((block) => /* @__PURE__ */ jsx(
      BlockRenderer,
      {
        block,
        contextData,
        components,
        componentProps,
        isInGrid: false
      },
      block.id
    )),
    /* @__PURE__ */ jsx("div", { className: cn(
      "mx-auto py-4 space-y-6",
      !fullWidth && "max-w-7xl"
    ), children: Object.keys(regularRows).sort((a, b) => Number(a) - Number(b)).map((rowKey) => {
      const rowAreas = regularRows[Number(rowKey)];
      return /* @__PURE__ */ jsx("div", { className: "w-full", children: /* @__PURE__ */ jsx("div", { className: "grid grid-cols-12 gap-4 lg:gap-6", children: rowAreas.sort((a, b) => a.grid_order - b.grid_order).map((area) => /* @__PURE__ */ jsx(
        AreaRenderer,
        {
          area,
          contextData,
          components,
          componentProps
        },
        area.id
      )) }) }, rowKey);
    }) })
  ] });
};
const AreaRenderer = React__default.memo(({
  area,
  contextData,
  components,
  componentProps
}) => {
  const getResponsiveClasses = () => {
    let classes = "col-span-12";
    if (area.responsive_config) {
      const responsive = area.responsive_config;
      if (responsive.sm) {
        classes += ` sm:${getGridSpanClass(responsive.sm)}`;
      }
      if (responsive.md) {
        classes += ` md:${getGridSpanClass(responsive.md)}`;
      }
      if (responsive.lg) {
        classes += ` lg:${getGridSpanClass(responsive.lg)}`;
      } else {
        classes += ` lg:${getGridSpanClass(area.grid_columns)}`;
      }
      if (responsive.xl) {
        classes += ` xl:${getGridSpanClass(responsive.xl)}`;
      }
    } else {
      classes += ` lg:${getGridSpanClass(area.grid_columns)}`;
    }
    return classes;
  };
  if (!area.blocks || area.blocks.length === 0) {
    return /* @__PURE__ */ jsx("div", { className: cn("space-y-4", getResponsiveClasses()), children: /* @__PURE__ */ jsx("div", { className: "text-center text-muted-foreground text-sm py-8", children: /* @__PURE__ */ jsxs("p", { children: [
      "No blocks configured for ",
      area.area_name
    ] }) }) });
  }
  const blockSpans = area.blocks.map((block) => block.grid_column_span || 12);
  const hasVariableGridSpans = blockSpans.length > 1 && new Set(blockSpans).size > 1;
  const hasNonFullWidthBlocks = area.blocks.some(
    (block) => block.grid_column_span && block.grid_column_span < 12
  );
  if (hasVariableGridSpans || hasNonFullWidthBlocks) {
    return /* @__PURE__ */ jsx("div", { className: cn(getResponsiveClasses()), children: /* @__PURE__ */ jsx("div", { className: "grid grid-cols-12 gap-4", children: area.blocks.sort((a, b) => a.ordering - b.ordering).map((block) => /* @__PURE__ */ jsx(
      BlockRenderer,
      {
        block,
        contextData,
        components,
        componentProps,
        isInGrid: true
      },
      block.id
    )) }) });
  }
  return /* @__PURE__ */ jsx("div", { className: cn("space-y-4", getResponsiveClasses()), children: area.blocks.sort((a, b) => a.ordering - b.ordering).map((block) => /* @__PURE__ */ jsx(
    BlockRenderer,
    {
      block,
      contextData,
      components,
      componentProps,
      isInGrid: false
    },
    block.id
  )) });
});
const BlockRenderer = React__default.memo(({
  block,
  contextData,
  components,
  componentProps,
  isInGrid = true
  // Default to true for backward compatibility
}) => {
  if (!block.enabled) {
    return null;
  }
  const config = block.block_config || {};
  const blockConfig = transformCompleteConfig(config || {});
  if (blockConfig.conditional) {
    const blockProps2 = componentProps?.[block.block_type] || {};
    const conditionValue = blockProps2[blockConfig.conditional];
    if (!conditionValue) {
      return null;
    }
  }
  const blockProps = componentProps?.[block.block_type] || {};
  if (blockProps.shouldRender !== void 0 && blockProps.shouldRender === false) {
    console.log(`Block ${block.block_type} skipped - shouldRender = false`);
    return null;
  }
  const isStickyOrFullWidth = blockConfig.stickyHeader === true || blockConfig.fullWidth === true;
  const getBlockClasses = () => {
    let classes = "";
    if (isInGrid) {
      if (block.responsive_config?.mobile_span) {
        classes += getGridSpanClass(block.responsive_config.mobile_span);
      } else {
        classes += "col-span-12";
      }
      if (block.responsive_config?.tablet_span) {
        classes += ` md:${getGridSpanClass(block.responsive_config.tablet_span)}`;
      }
      if (block.responsive_config?.desktop_span) {
        classes += ` lg:${getGridSpanClass(block.responsive_config.desktop_span)}`;
      } else if (block.grid_column_span) {
        classes += ` lg:${getGridSpanClass(block.grid_column_span)}`;
      }
    } else {
      classes += "w-full";
    }
    if (blockConfig.cssClass) {
      classes += ` ${blockConfig.cssClass}`;
    }
    if (block.responsive_config) {
      const responsive = block.responsive_config;
      if (responsive.hidden_sm) classes += " sm:hidden";
      if (responsive.hidden_md) classes += " md:hidden";
      if (responsive.hidden_lg) classes += " lg:hidden";
      if (responsive.hidden_xl) classes += " xl:hidden";
    }
    return classes;
  };
  const getBlockStyles = () => {
    const styles = {};
    if (blockConfig.backgroundColor) {
      styles.backgroundColor = blockConfig.backgroundColor;
    }
    if (blockConfig.padding) {
      styles.padding = blockConfig.padding;
    }
    if (blockConfig.margin) {
      styles.margin = blockConfig.margin;
    }
    return styles;
  };
  const getCustomAttributes = () => {
    const attributes = {};
    if (blockConfig.customAttributes) {
      try {
        const customAttrs = typeof blockConfig.customAttributes === "string" ? JSON.parse(blockConfig.customAttributes) : blockConfig.customAttributes;
        if (typeof customAttrs === "object" && customAttrs !== null) {
          Object.assign(attributes, customAttrs);
        }
      } catch (error) {
        console.warn(`Invalid custom attributes for block ${block.id}:`, error);
      }
    }
    return attributes;
  };
  const blockComponentProps = componentProps?.[block.block_type] || {};
  const mergedData = {
    ...blockConfig,
    // Use transformed config instead of raw
    ...contextData,
    ...blockComponentProps
    // Add component-specific props (this includes categories data)
  };
  const blockRegistryComponent = renderBlock(
    block.block_type,
    mergedData,
    `block_${block.id}`,
    getBlockClasses()
  );
  if (blockRegistryComponent) {
    if (isStickyOrFullWidth) {
      return blockRegistryComponent;
    }
    return /* @__PURE__ */ jsx(
      "div",
      {
        className: cn(getBlockClasses()),
        style: getBlockStyles(),
        ...getCustomAttributes(),
        children: blockRegistryComponent
      }
    );
  }
  const Component = components?.[block.block_type];
  if (!Component) {
    return /* @__PURE__ */ jsx("div", { className: "p-4 border-2 border-dashed border-muted rounded-lg", children: /* @__PURE__ */ jsxs("div", { className: "text-center text-muted-foreground", children: [
      /* @__PURE__ */ jsxs("p", { className: "text-sm", children: [
        'Block type "',
        block.block_type,
        '" not found'
      ] }),
      /* @__PURE__ */ jsxs("p", { className: "text-xs mt-1", children: [
        "Expected component: ",
        block.block_name
      ] })
    ] }) });
  }
  const props = {
    ...componentProps?.[block.block_type],
    config: {
      ...componentProps?.[block.block_type]?.config,
      ...blockConfig,
      // Use the same transformed config
      ...contextData
    },
    blockId: block.id,
    blockName: block.block_name,
    responsive: block.responsive_config
  };
  if (isStickyOrFullWidth) {
    return /* @__PURE__ */ jsx(Component, { ...props });
  }
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(getBlockClasses()),
      style: getBlockStyles(),
      ...getCustomAttributes(),
      children: /* @__PURE__ */ jsx(Component, { ...props })
    }
  );
});
const dynamicLayoutManager = React__default.memo(DynamicLayoutManager);
export {
  dynamicLayoutManager as default
};
//# sourceMappingURL=dynamic-layout-manager.js.map
